import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api } from "../lib/api";
import { CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";

export default function PaymentReturn() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const taskId = params.get("task_id");
  const [state, setState] = useState({ phase: "polling", attempts: 0, error: null, data: null });
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState({ phase: "error", error: "Missing session id" });
      return;
    }
    let cancelled = false;
    const maxAttempts = 10;
    const tick = async () => {
      try {
        const { data } = await api.get(`/payments/checkout/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setState({ phase: "paid", data });
          // Redirect to task after a moment
          setTimeout(() => navigate(`/task/${data.task_id || taskId}`), 1500);
          return;
        }
        if (data.status === "expired") {
          setState({ phase: "expired", data });
          return;
        }
        attemptsRef.current += 1;
        if (attemptsRef.current >= maxAttempts) {
          setState({ phase: "timeout", data });
          return;
        }
        setTimeout(tick, 1800);
      } catch (e) {
        if (cancelled) return;
        setState({ phase: "error", error: e.response?.data?.detail || e.message });
      }
    };
    tick();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="max-w-md mx-auto" data-testid="payment-return-page">
      <div className="brut-card p-8 text-center pop-in">
        {state.phase === "polling" && (
          <>
            <Loader2 className="mx-auto animate-spin text-[var(--cs-money-2)]" size={42} />
            <h1 className="font-display text-3xl font-black mt-4">Confirming your payment…</h1>
            <p className="text-[var(--cs-ink-2)] mt-2">This usually takes a few seconds.</p>
          </>
        )}
        {state.phase === "paid" && (
          <>
            <CheckCircle2 className="mx-auto text-[var(--cs-money-2)]" size={48} />
            <h1 className="font-display text-3xl font-black mt-4">Payment received!</h1>
            <p className="text-[var(--cs-ink-2)] mt-2">Your task is live. Taking you there now…</p>
            <div className="mt-4 font-display tabular text-4xl font-black text-[var(--cs-money-2)]">
              ${state.data?.amount?.toFixed(2)}
            </div>
          </>
        )}
        {state.phase === "expired" && (
          <>
            <XCircle className="mx-auto text-red-600" size={42} />
            <h1 className="font-display text-3xl font-black mt-4">Payment expired</h1>
            <p className="text-[var(--cs-ink-2)] mt-2">No worries — try again from the post page.</p>
            <Link to="/post" className="brut-btn brut-btn-money mt-5 inline-flex" data-testid="retry-post">
              Retry <ArrowRight size={16} />
            </Link>
          </>
        )}
        {state.phase === "timeout" && (
          <>
            <Loader2 className="mx-auto text-[var(--cs-ink-2)]" size={42} />
            <h1 className="font-display text-3xl font-black mt-4">Still processing</h1>
            <p className="text-[var(--cs-ink-2)] mt-2">Stripe is taking longer than expected. You'll see the task update soon.</p>
            <Link to="/my-tasks" className="brut-btn mt-5 inline-flex" data-testid="check-my-tasks">
              Check my tasks
            </Link>
          </>
        )}
        {state.phase === "error" && (
          <>
            <XCircle className="mx-auto text-red-600" size={42} />
            <h1 className="font-display text-3xl font-black mt-4">Something went wrong</h1>
            <p className="text-[var(--cs-ink-2)] mt-2">{String(state.error)}</p>
            <Link to="/post" className="brut-btn mt-5 inline-flex" data-testid="back-post">
              Back to post
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
